/**
 * Washing line (`hush.washing_line.v1`) — a ground piece for the yard beside the hideout.
 *
 * Frame 180×110, support at the bottom-center (90, 110). Two poles lean slightly outward from a trodden grass
 * strip, a rope sags between their tops, and `items` (3..5) pieces of cloth hang pegged on it: shirts, a blanket,
 * a pair of socks, a towel — in muted, washed-out colours (no saturated clue-blue ribbon). `breeze` (0..1) swings
 * the hems to the right and ripples them. No buttons, holes or paired dots on the cloth (anti-face rule).
 * No definitions, no collision or gameplay geometry. Deterministic for the recipe; the seed varies the laundry.
 */
import {
  checkEnvelope, checkScope, el, mix, n, partGroup, poly, rngFor,
  type HideoutPieceArt, type PieceRecipe, type Point, type ResolvedPalette,
} from '../pieces/svg';

export interface WashlineParams {
  readonly items: number;  // 3..5 integer
  readonly breeze: number; // 0..1
}
export type WashlineRecipe = PieceRecipe<'hush.washing_line.v1', WashlineParams>;

export const WASHLINE_FRAME = { w: 180, h: 110 } as const;

export const HUSH_WASHLINE: WashlineRecipe = {
  schema: 'hush.asset-recipe.v1', id: 'hush.washing_line.yard.v1', generatorId: 'hush.washing_line.v1',
  seed: 20067018, paletteId: 'coastal_salt', params: { items: 4, breeze: 0.4 },
};

/** rngFor's first draw is correlated across similar channel names; a murmur finalizer decorrelates it. */
function scrambled(base: (ch: string) => number): (ch: string) => number {
  return ch => {
    let h = Math.floor(base(ch) * 4294967296) >>> 0;
    h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  };
}

function num(p: unknown, k: string, lo: number, hi: number, int = false): number {
  const v = (p as Record<string, unknown>)[k];
  if (typeof v !== 'number' || !(v >= lo && v <= hi) || (int && !Number.isInteger(v))) {
    throw new Error(`hush.washing_line.v1: ${k} must be ${int ? 'an integer ' : ''}${lo}..${hi}`);
  }
  return v;
}

const W = WASHLINE_FRAME.w, H = WASHLINE_FRAME.h;
const clamp = (p: Point): Point => ({ x: Math.min(W - 2, Math.max(2, p.x)), y: Math.min(H - 3, Math.max(2, p.y)) });

type Kind = 'shirt' | 'blanket' | 'socks' | 'towel';

export function buildWashline(recipe: WashlineRecipe, roles: ResolvedPalette, scope: string): HideoutPieceArt {
  checkEnvelope(recipe, 'hush.washing_line.v1');
  checkScope(scope);
  const items = num(recipe.params, 'items', 3, 5, true);
  const breeze = num(recipe.params, 'breeze', 0, 1);
  const rnd = scrambled(rngFor(recipe));
  const parts: string[] = [];
  const outline = roles.outline;

  // ---- poles: lean slightly outward, a crossbar notch at the top ----
  const poleBaseY = H - 4;
  const lb = { x: 20, y: poleBaseY }, rb = { x: W - 20, y: poleBaseY };
  const lt = { x: 14 - rnd('pole.l') * 3, y: 14 + rnd('pole.l.h') * 4 };
  const rt = { x: W - 14 + rnd('pole.r') * 3, y: 14 + rnd('pole.r.h') * 4 };
  const pole = (b: Point, t: Point, side: number): string => {
    const hw0 = 3, hw1 = 2.1;
    const shape = poly([{ x: b.x - hw0, y: b.y }, { x: t.x - hw1, y: t.y }, { x: t.x + hw1, y: t.y - 0.6 * side }, { x: b.x + hw0, y: b.y }]);
    return el('path', { d: shape, fill: roles.woodBase, stroke: outline, 'stroke-width': 1.2, 'stroke-linejoin': 'round' })
      + el('path', { d: poly([{ x: b.x - hw0 + 1.2, y: b.y - 2 }, { x: t.x - hw1 + 1, y: t.y + 3 }], false), fill: 'none', stroke: roles.woodLight, 'stroke-width': 0.9, opacity: 0.8 })
      + el('path', { d: poly([{ x: t.x - 4.5, y: t.y + 4 }, { x: t.x + 4.5, y: t.y + 3 }], false), fill: 'none', stroke: roles.woodShadow, 'stroke-width': 2, 'stroke-linecap': 'round' });
  };
  const poles = pole(lb, lt, -1) + pole(rb, rt, 1);

  // ---- rope: quadratic sag between the pole tops ----
  const ra = { x: lt.x + 1, y: lt.y + 3.5 }, rbp = { x: rt.x - 1, y: rt.y + 3.5 };
  const sag = 12 + rnd('rope.sag') * 6;
  const rc = { x: W / 2, y: (ra.y + rbp.y) / 2 + sag * 2 };
  const rope = (t: number): Point => ({
    x: (1 - t) ** 2 * ra.x + 2 * (1 - t) * t * rc.x + t * t * rbp.x,
    y: (1 - t) ** 2 * ra.y + 2 * (1 - t) * t * rc.y + t * t * rbp.y,
  });
  const ropePath = `M${n(ra.x)} ${n(ra.y)}Q${n(rc.x)} ${n(rc.y)} ${n(rbp.x)} ${n(rbp.y)}`;
  const ropeArt = el('path', { d: ropePath, fill: 'none', stroke: outline, 'stroke-width': 1.6, 'stroke-linecap': 'round' })
    + el('path', { d: ropePath, fill: 'none', stroke: roles.paperShadow, 'stroke-width': 0.7, 'stroke-linecap': 'round' });

  // ---- laundry: muted colours, each item hung at a slot along the rope ----
  const fills = [
    { base: mix(roles.clothBlue, roles.paperShadow, 0.55), shade: mix(roles.clothBlueShadow, roles.paperShadow, 0.45) },
    { base: roles.paperBase, shade: roles.paperShadow },
    { base: mix(roles.reedLight, roles.paperBase, 0.45), shade: mix(roles.reedBase, roles.paperShadow, 0.5) },
    { base: mix(roles.woodLight, roles.paperBase, 0.4), shade: mix(roles.woodBase, roles.paperShadow, 0.4) },
    { base: mix(roles.stoneLight, roles.paperBase, 0.3), shade: roles.stoneBase },
  ];
  const kinds: Kind[] = ['shirt', 'blanket', 'socks', 'towel'];
  const lean = breeze * 9; // hem shift to the right at full breeze
  let laundry = '', pegs = '';
  const first = Math.floor(rnd('fill.start') * fills.length);
  for (let i = 0; i < items; i++) {
    const t = 0.12 + 0.76 * ((i + 0.5) / items) + (rnd(`item.${i}.t`) - 0.5) * 0.05;
    const kind = i === 1 && items >= 4 ? 'blanket' : kinds[Math.floor(rnd(`item.${i}.kind`) * kinds.length)];
    const c = fills[(first + i * 2) % fills.length];
    const slot = (W - 44) / items;
    const wide = kind === 'blanket' ? slot * 0.95 : kind === 'socks' ? slot * 0.55 : slot * (0.62 + rnd(`item.${i}.w`) * 0.18);
    const half = Math.min(wide, 40) / 2;
    const p = rope(t);
    // Top edge follows the rope locally.
    const tl = rope(Math.max(0, t - half / (W - 30)));
    const tr = rope(Math.min(1, t + half / (W - 30)));
    const topL = { x: p.x - half, y: tl.y + 0.4 }, topR = { x: p.x + half, y: tr.y + 0.4 };
    const drop = kind === 'blanket' ? 44 + rnd(`item.${i}.d`) * 10 : kind === 'socks' ? 22 : kind === 'towel' ? 30 + rnd(`item.${i}.d`) * 6 : 34;
    const sway = lean * (0.6 + rnd(`item.${i}.s`) * 0.6);
    const flutter = (k: number) => Math.sin(k * 1.9 + rnd(`item.${i}.ph`) * 6) * (0.6 + breeze * 1.8);
    /** Hem points from x1 back to x0 (right→left); reverse for a left→right outline. */
    const hem = (x0: number, x1: number, y: number, steps: number): Point[] => {
      const pts: Point[] = [];
      for (let k = 0; k <= steps; k++) pts.push(clamp({ x: x1 + (x0 - x1) * (k / steps) + sway, y: y + flutter(k) }));
      return pts;
    };
    const stroke = { stroke: outline, 'stroke-width': 1.1, 'stroke-linejoin': 'round' } as const;
    let art = '';
    if (kind === 'shirt') {
      const bodyBottom = Math.max(topL.y, topR.y) + drop;
      const sleeve = half * 0.55;
      const shape = [
        topL, { x: topL.x - sleeve + sway * 0.3, y: topL.y + 9 }, { x: topL.x - sleeve * 0.55 + sway * 0.4, y: topL.y + 15 },
        { x: topL.x + 2 + sway * 0.5, y: topL.y + 11 },
        ...hem(topL.x + 2, topR.x - 2, bodyBottom, 5).reverse(),
        { x: topR.x - 2 + sway * 0.5, y: topR.y + 11 }, { x: topR.x + sleeve * 0.55 + sway * 0.4, y: topR.y + 15 },
        { x: topR.x + sleeve + sway * 0.3, y: topR.y + 9 }, topR,
        { x: p.x + half * 0.3, y: p.y + 0.6 }, { x: p.x, y: p.y + 5 }, { x: p.x - half * 0.3, y: p.y + 0.6 },
      ].map(clamp);
      art += el('path', { d: poly(shape), fill: c.base, ...stroke });
      art += el('path', { d: poly([{ x: topL.x + 4, y: topL.y + 13 }, { x: topL.x + 5 + sway * 0.7, y: bodyBottom - 3 }].map(clamp), false), fill: 'none', stroke: c.shade, 'stroke-width': 1.4, opacity: 0.8 });
    } else if (kind === 'socks') {
      for (const dx of [-half * 0.5, half * 0.5]) {
        const x = p.x + dx, y = rope(t + dx / (W - 30)).y + 0.4;
        const toe = dx < 0 ? -1 : 1;
        const sh = [
          { x: x - 3, y }, { x: x + 3, y },
          { x: x + 3 + sway * 0.6, y: y + drop - 6 }, { x: x + 3 + toe * 5 + sway * 0.7, y: y + drop - 2 },
          { x: x + toe * 4 + sway * 0.7, y: y + drop + 1 }, { x: x - 3 + sway * 0.6, y: y + drop - 1 },
        ].map(clamp);
        art += el('path', { d: poly(sh), fill: c.base, ...stroke });
        art += el('path', { d: poly([{ x: x - 3, y: y + 4 }, { x: x + 3, y: y + 4 }].map(clamp), false), fill: 'none', stroke: c.shade, 'stroke-width': 1.6 });
      }
    } else {
      const bottom = Math.max(topL.y, topR.y) + drop;
      art += el('path', { d: poly([topL, ...hem(topL.x, topR.x, bottom, 7).reverse(), topR].map(clamp)), fill: c.base, ...stroke });
      if (kind === 'blanket') {
        // Two broad woven stripes, a soft fold shadow.
        for (const f of [0.35, 0.62]) {
          art += el('path', {
            d: poly([{ x: topL.x + 1.5 + sway * f, y: topL.y + drop * f }, { x: topR.x - 1.5 + sway * f, y: topR.y + drop * f }].map(clamp), false),
            fill: 'none', stroke: c.shade, 'stroke-width': 3, opacity: 0.75,
          });
        }
      } else {
        art += el('path', {
          d: poly([{ x: topL.x + 1.5 + sway * 0.85, y: bottom - 5 }, { x: topR.x - 1.5 + sway * 0.85, y: bottom - 5 }].map(clamp), false),
          fill: 'none', stroke: c.shade, 'stroke-width': 1.4, opacity: 0.8,
        });
      }
      art += el('path', {
        d: poly([{ x: p.x + half * 0.2, y: p.y + 4 }, { x: p.x + half * 0.25 + sway * 0.8, y: bottom - 4 }].map(clamp), false),
        fill: 'none', stroke: c.shade, 'stroke-width': 1.2, opacity: 0.55,
      });
    }
    laundry += art;
    // Pegs: thin slanted wood clips straddling the rope at the item's top corners (never a matched dot pair).
    for (const [k, q] of [[0, topL], [1, topR]] as const) {
      if (kind === 'socks') break;
      const x = q.x + (k ? -2.5 : 2.5), y = q.y;
      const tilt = (rnd(`peg.${i}.${k}`) - 0.5) * 3;
      pegs += el('path', { d: poly([{ x: x - 1.1, y: y - 3.5 }, { x: x + 1.1, y: y - 3.5 }, { x: x + 1.1 + tilt, y: y + 4 }, { x: x - 1.1 + tilt, y: y + 4 }].map(clamp)),
        fill: roles.woodLight, stroke: roles.woodShadow, 'stroke-width': 0.7 });
    }
    if (kind === 'socks') {
      const x = p.x, y = p.y;
      pegs += el('path', { d: poly([{ x: x - 1.1, y: y - 3.5 }, { x: x + 1.1, y: y - 3.5 }, { x: x + 1.1, y: y + 4 }, { x: x - 1.1, y: y + 4 }].map(clamp)),
        fill: roles.woodLight, stroke: roles.woodShadow, 'stroke-width': 0.7 });
    }
  }

  // ---- ground: a trodden grass strip across the bottom that both poles and the support sit in ----
  const groundTop = H - 7;
  let ground = el('path', {
    d: `M10 ${n(H - 0.5)}Q${n(W * 0.3)} ${n(groundTop - 1)} ${n(W / 2)} ${n(groundTop)}Q${n(W * 0.7)} ${n(groundTop - 1)} ${n(W - 10)} ${n(H - 0.5)}Z`,
    fill: mix(roles.reedShadow, roles.paperShadow, 0.3), stroke: outline, 'stroke-width': 1, 'stroke-linejoin': 'round',
  });
  for (let i = 0; i < 16; i++) {
    const x = 16 + i * ((W - 32) / 15) + (rnd(`tuft.${i}`) - 0.5) * 3;
    const h = 5 + rnd(`tuft.${i}.h`) * 6;
    ground += el('path', { d: `M${n(x - 1.5)} ${n(H - 0.6)}Q${n(x)} ${n(H - h * 0.6)} ${n(x + (i % 2 ? 2.2 : -2.2))} ${n(H - h)}Q${n(x + 0.5)} ${n(H - h * 0.4)} ${n(x + 1.5)} ${n(H - 0.6)}Z`,
      fill: i % 3 ? roles.reedBase : roles.reedLight, stroke: outline, 'stroke-width': 0.5 });
  }

  const body = partGroup(parts, 'washline-ground', ground)
    + partGroup(parts, 'washline-poles', poles)
    + partGroup(parts, 'washline-rope', ropeArt)
    + partGroup(parts, 'washline-laundry', laundry)
    + partGroup(parts, 'washline-pegs', pegs);
  const support = { x: W / 2, y: H };
  const mid = rope(0.5);
  return {
    frame: { ...WASHLINE_FRAME },
    body,
    anchors: { support, pole_left: { x: lb.x, y: lb.y }, pole_right: { x: rb.x, y: rb.y }, rope_mid: { x: mid.x, y: mid.y } },
    parts,
  };
}
